import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { Assistance } from '../../../../../models/assistance'
import { WorkerAvailability } from '../../../../../models/workerAvailability'
import { AssistanceStatus } from '../../../../../models/assistanceStatus'
import {
  IonicSelectableCloseButtonTemplateDirective,
  IonicSelectableComponent,
  IonicSelectableFooterTemplateDirective,
  IonicSelectableHeaderTemplateDirective,
  IonicSelectableIconTemplateDirective,
  IonicSelectableItemTemplateDirective,
  IonicSelectableSearchFailTemplateDirective,
  IonicSelectableValueTemplateDirective,
} from 'ionic-selectable'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, ReplaySubject, Subject } from 'rxjs'
import { WorkerAvailabilitySelectItem, WorkerAvailabilitySelectItemFlag } from './WorkerAvailabilitySelectItem'
import { filter, first, map, skipWhile, switchMap, takeUntil, tap } from 'rxjs/operators'
import { AssistanceService } from '../../../../../services/domain/assistance/assistance.service'
import { ClientService } from '../../../../../services/domain/client/client.service'
import {
  WorkerAvailabilitiesRecommendationService,
} from '../../../../../services/worker-availabilities-recommendation.service'
import {
  IRecommendedAssistantsFilter,
  RecommendedAssistantsFilter,
} from '../../../../assistance/assistance-editor/recommended-assistants-filter'
import { SearchUtils } from '../../../../../shared/utils/searchUtils'
import { ListAssistanceLockService } from '../../../../assistance/assistance-list-new/list-assistance-lock.service'
import { DateRange, DateUtils } from '../../../../../services/date-utils.service'
import { FeatureFlags } from '../../../../../shared/featureFlags'
import { differenceInMinutes } from 'date-fns'
import { UsersService } from '../../../../../services/domain/users.service'
import { NgxFloatUiContentComponent } from 'ngx-float-ui'
import { AssistantService } from '../../../../../services/domain/assistant/assistant.service'
import { FloatoverService } from '../../../../floatover/floatover.service'
import { faFilter, faSlash } from '@fortawesome/free-solid-svg-icons'
import { AvailabilitySelectItemsStash } from './AvailabilitySelectItemsStash'
import { TouchscreenService } from '../../../../../services/touchscreen.service'
import { IonicModule } from '@ionic/angular'
import { AsyncPipe, DatePipe, NgClass } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { GenderSelectComponent } from '../../../../assistance/assistance-editor/gender-select/gender-select.component'
import { log } from '../../../../../shared/utils/rxjsUtils'
import { WorkerStatus } from '../../../../../models/worker-status'
import { FeathersService } from '../../../../../services/feathers.service'

type WorkerSelectMode = 'availabilities' | 'allAssistants'

@Component({
  selector: 'app-worker-select',
  templateUrl: './worker-select.component.html',
  styleUrls: ['./worker-select.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    DatePipe,
    NgClass,
    FormsModule,
    AsyncPipe,
    FontAwesomeModule,
    GenderSelectComponent,
    IonicSelectableComponent,
    IonicSelectableSearchFailTemplateDirective,
    IonicSelectableValueTemplateDirective,
    IonicSelectableIconTemplateDirective,
    IonicSelectableFooterTemplateDirective,
    IonicSelectableItemTemplateDirective,
    IonicSelectableCloseButtonTemplateDirective,
    IonicSelectableHeaderTemplateDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class WorkerSelectComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private feathers = inject(FeathersService)
  private assistanceService = inject(AssistanceService)
  private availabilitiesRecommender = inject(WorkerAvailabilitiesRecommendationService)
  private clientsService = inject(ClientService)
  private assistanceLocker = inject(ListAssistanceLockService)
  private userService = inject(UsersService)
  private assistantService = inject(AssistantService)
  private floatoverService = inject(FloatoverService)
  private touchscreenService = inject(TouchscreenService)

  @Input() assistanceURID: string
  @Input() assistanceRange: DateRange // Optional. Will fallback to assistance's planned range.
  @Input() isEnabled: boolean
  @Input() isModal = false
  @Input() hideStatusButtons: boolean = false
  @Input() filterOverrides: Partial<IRecommendedAssistantsFilter> = {}

  @Output() availabilitiesChanged = new EventEmitter<{
    assistance: Assistance
    availabilities: WorkerAvailability[]
  }>()
  @Output() availabilitiesRemoved = new EventEmitter<{ assistanceId: number; assistance: Assistance }>()
  @Output() statusChanged = new EventEmitter<{
    availability: WorkerAvailability
    status: AssistanceStatus
  }>()
  @Output() statusClicked = new EventEmitter<WorkerAvailability>()
  @Output() closed = new EventEmitter()
  @Output() closedWithoutChanges = new EventEmitter()

  @ViewChild('workerSelectComponent')
  workerSelectComponent: IonicSelectableComponent
  @ViewChild('tooltip') tooltip: NgxFloatUiContentComponent

  private TAG: string = 'WorkerSelectInternalComponent'

  private unsubscribe: Subject<void> = new Subject<void>()

  private assistanceURID$: ReplaySubject<string> = new ReplaySubject<string>(1)
  private filterChanged$: ReplaySubject<RecommendedAssistantsFilter> = new ReplaySubject<RecommendedAssistantsFilter>(1)
  selectMode$: BehaviorSubject<WorkerSelectMode> = new BehaviorSubject<WorkerSelectMode>('availabilities')
  private onSearch$: BehaviorSubject<{
    component: IonicSelectableComponent
    text: string
  }> = new BehaviorSubject({ component: null, text: '' })

  assistance: Assistance
  selectItemsStash = new AvailabilitySelectItemsStash([])
  availabilityItemStash = new AvailabilitySelectItemsStash()

  itemsCurrentlyRendered: WorkerAvailabilitySelectItem[]
  itemsCurrentlyRenderedDisabled: WorkerAvailabilitySelectItem[]

  itemsSelected: WorkerAvailabilitySelectItem[] = []

  recommendedAssistantsFilter: RecommendedAssistantsFilter

  confirming = false

  isTouchscreen = false

  data$: Observable<unknown>
  private open$ = new BehaviorSubject<any>('')

  assistanceAroundInfoLoading = false
  availabilitiesLoading = false

  faFilter = faFilter
  faSlash = faSlash

  protected readonly WorkerAvailabilitySelectItemFlag = WorkerAvailabilitySelectItemFlag
  private modalClosing = false

  constructor() {
    this.data$ = this.loadData()
    this.isTouchscreen = this.touchscreenService.isTouchscreen
  }

  ngOnInit(): void {}

  ngAfterViewInit() {
    this.data$.subscribe((e) => console.log('data$', e))
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName) && propName === 'assistanceURID' && !!changes.assistanceURID.currentValue) {
        this.assistanceURID$.next(changes.assistanceURID.currentValue)
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next()
    this.unsubscribe.complete()
  }

  /**
   * Sets up the assistance for all further operations.
   * Handles nonrecurrent and recurrent assistances, using URID.
   * @private
   */
  private setupAssistanceFromURID(urid: string): Observable<Assistance> {
    return this.assistanceService.getByURID(urid).pipe(map((a) => (this.assistance = a)))
  }

  protected getAssistanceRange(): DateRange {
    if (this.assistanceRange) return this.assistanceRange
    else if (this.assistance)
      return {
        // Custom date range takes precendence
        from: this.assistance.getDateFrom(),
        to: this.assistance.getDateTo(),
      } // Fallback to assistance's planned range
    return null
  }

  switchSelectMode(mode: WorkerSelectMode) {
    this.selectMode$.next(mode)
  }

  private setItemsSelectedFromAssistance(assistance: Assistance) {
    if (!assistance.assistantAssignments) return

    this.itemsSelected = assistance.assistantAssignments.getAvailabilitiesWithStatus(true).map((assignment) => {
      return new WorkerAvailabilitySelectItem({
        availability: assignment.availability,
        status: assignment.status,
        enabled: true,
      })
    })

    if (this.workerSelectComponent) this.workerSelectComponent._selectedItems = this.itemsSelected
  }

  private addToSelectedItems(items: WorkerAvailabilitySelectItem[]) {
    this.itemsSelected.push(
      ...items.filter((itemToAdd) => !this.itemsSelected.some((i) => i.availability.id === itemToAdd.availability.id)),
    )

    if (this.workerSelectComponent) this.workerSelectComponent._selectedItems = this.itemsSelected
  }

  private setItemsRendered(items: WorkerAvailabilitySelectItem[]) {
    this.itemsCurrentlyRendered = items
    this.itemsCurrentlyRenderedDisabled = items?.filter((i) => !i.enabled)
  }

  onSearch(event: { component: IonicSelectableComponent; text: string }) {
    this.onSearch$.next(event)
  }

  private search(
    items: WorkerAvailabilitySelectItem[],
    event: { component: IonicSelectableComponent; text: string },
  ) {
    event.component.startSearch()
    const filtered = this.filterAvailabilitiesByWorkerName(items, event.text)
    event.component.endSearch()

    return filtered
  }

  private filterAvailabilitiesByWorkerName(allAvailabilityItems: WorkerAvailabilitySelectItem[], text: string) {
    if (!text.length) return allAvailabilityItems
    return allAvailabilityItems.filter((a) =>
      SearchUtils.includeFilter(a.availability.worker.user.fullName(), text),
    )
  }

  onIonicSelectableClose($event: { component: IonicSelectableComponent }) {
    this.close()
    this.confirming = false
  }

  private close(withChanges = true) {
    if (this.workerSelectComponent.isOpened) {
      this.workerSelectComponent.close()
      if (this.isModal) {
        this.modalClosing = true
        withChanges ? this.closed.emit() : this.closedWithoutChanges.emit()
      }
      this.confirming = false
    }
  }

  onAssistantModalCloseByCrossButton() {
    if (this.workerSelectComponent.isOpened) this.close(false)
  }

  confirm() {
    if (!this.confirming) {
      this.confirming = true
      this.workerSelectComponent.confirm()
      this.save()
    }
  }

  async save() {
    this.assistanceLocker.lock(this.assistance)

    const availabilities: WorkerAvailability[] = this.itemsSelected.map((i) => i.availability)

    const emitValue = { assistance: this.assistance, availabilities }

    this.availabilitiesChanged.emit(emitValue)

    this.close()
  }

  public clearAssistant() {
    this.availabilitiesRemoved.emit({ assistanceId: this.assistance.id, assistance: this.assistance })
    this.clear()
  }

  public clear() {
    this.workerSelectComponent.clear()
    if (this.workerSelectComponent.isOpened) this.close()
  }

  onRecommendedAssistantsFilterChange() {
    this.filterChanged$.next(this.recommendedAssistantsFilter)
  }

  onStatusClicked(availability: WorkerAvailability) {
    this.statusClicked.emit(availability)
  }

  private initFilter(assistance: Assistance) {
    this.recommendedAssistantsFilter = RecommendedAssistantsFilter.createFromAssistance(
      assistance,
      this.filterOverrides,
    )
    this.onRecommendedAssistantsFilterChange()
  }

  resetFilterToAssistanceDefaults() {
    this.recommendedAssistantsFilter.resetToDefaults()
    this.onRecommendedAssistantsFilterChange()
  }

  private mergeSelectedWithRecommended(
    mode: WorkerSelectMode,
    recommendedItems: WorkerAvailabilitySelectItem[],
    selectedItems: WorkerAvailabilitySelectItem[],
  ) {
    const toRender: WorkerAvailabilitySelectItem[] = []

    toRender.push(...selectedItems)

    toRender.push(
      ...recommendedItems.filter(
        (rec) =>
          !selectedItems.some((sel) => {
            return (
              sel.availability.worker.assistant.id === rec.availability.worker.assistant.id &&
              sel.availability.dt_start?.getTime() === rec.availability.dt_start?.getTime() && // null and undefined have to be the same
              sel.availability.dt_end?.getTime() === rec.availability.dt_end?.getTime()
            ) // null and undefined have to be the same
          }),
      ),
    )

    return toRender.sort((a, b) => WorkerAvailability.sortByAssistantName(a.availability, b.availability))
  }

  private filterOutFakeAvailabilitiesWhenThereIsARealOne(
    availabilities: WorkerAvailabilitySelectItem[],
    fakeAvails: WorkerAvailabilitySelectItem[],
  ) {
    return fakeAvails.filter((fa) => {
      return !availabilities.some((a) => fa.availability.worker.assistant.id === a.availability.worker.assistant.id)
    })
  }

  // Caution: Calling open() manually on ionicSelectable does not fire onOpen event.
  private open() {
    this.workerSelectComponent.open()
  }

  onOpen() {
    this.open$.next('')
  }

  public isClosed() {
    return !this.workerSelectComponent.isOpened
  }

  private async addAssistancesBeforeAndAfterToRenderedItems(
    assistance: Assistance,
    availabilityItems: WorkerAvailabilitySelectItem[],
  ) {
    if (!FeatureFlags.workerSelect_showAssistancesAround) return availabilityItems

    const user = await this.userService.getCurrentUserPromise()
    if (!user.config_allow_worker_select_before_after) return availabilityItems

    // Pro kazdeho asistenta v items je potreba stahnout assignmenty => assistance plannedFrom-To casy
    // 1. Takze potrebuju idcka asistentu
    const assistantIds: number[] = []

    for (const i of availabilityItems) {
      if (i.enabled) assistantIds.push(i.availability.worker.assistant.id)
    }

    // 2. Stahnout asistence v danem casovem rozmezi, ktere maji nektere z idcek asistentu
    this.assistance.getDateFrom()
    const range: DateRange = this.getAssistanceRange()

    ;(await this.assistanceService.getAssistancesAroundRangeForAssistants(range, 180, assistantIds))
      .pipe(first())
      .subscribe((assistances) => {
        // Pro kazdeho asistenta (item) najit odpovidajici asistence around a navesit je
        for (const i of availabilityItems) {
          const assistancesForAssistant = assistances.filter((a) =>
            a.assistantAssignments.hasAssistant(i.availability.worker.assistant.id),
          )

          for (const a of assistancesForAssistant) {
            if (a.getDateFrom() >= assistance.getDateTo()) {
              // Check jestli uz nahodou nemame nejakou blizsi assistanceAfter nez tato
              if (!i.assistanceAfter || i.assistanceAfter.getDateFrom() > a.getDateFrom()) {
                i.assistanceAfter = a
                i.assistanceAfterHHMM = differenceInMinutes(a.getDateFrom(), assistance.getDateTo()).toString(10)
              }
            }
            if (a.getDateTo() <= assistance.getDateFrom()) {
              // Check jestli uz nahodou nemame nejakou blizsi assistanceBefore nez tato
              if (!i.assistanceBefore || i.assistanceBefore.getDateTo() < a.getDateTo()) {
                i.assistanceBefore = a
                i.assistanceBeforeHHMM = differenceInMinutes(a.getDateTo(), assistance.getDateFrom()).toString(10)
              }
            }
          }
        }
      })
  }

  async showAssistanceBeforeInfo(availItem: WorkerAvailabilitySelectItem, event?: MouseEvent) {
    const currentMouseX = event?.pageX || 0
    const currentMouseY = event?.pageY || 0

    this.floatoverService.setLoading(true)

    this.floatoverService.setPosition(currentMouseX, currentMouseY)
    this.floatoverService.show()

    this.clientsService
      .getById(availItem.assistanceBefore.clientId)
      .pipe(first())
      .subscribe((client) => {
        const timeFrom = DateUtils.dateToHhColonMm(availItem.assistanceBefore.getDateFrom())
        const timeTo = DateUtils.dateToHhColonMm(availItem.assistanceBefore.getDateTo())

        this.floatoverService.setLoading(false)
        this.floatoverService.setContentHtml(
          `<div>${client.user.fullName()}</div><div><strong>${timeFrom}-${timeTo}</strong></div><div>${availItem.assistanceBefore.addressEnd}</div>`,
        )
      })
  }

  hideAssistanceAroundInfo() {
    this.floatoverService.hide()
  }

  showAssistanceAfterInfo(availItem: WorkerAvailabilitySelectItem, event?: MouseEvent) {
    const currentMouseX = event?.pageX || 0
    const currentMouseY = event?.pageY || 0

    this.floatoverService.setLoading(true)

    this.floatoverService.setPosition(currentMouseX, currentMouseY)
    this.floatoverService.show()

    this.clientsService
      .getById(availItem.assistanceAfter.clientId)
      .pipe(first())
      .subscribe((client) => {
        const timeFrom = DateUtils.dateToHhColonMm(availItem.assistanceAfter.getDateFrom())
        const timeTo = DateUtils.dateToHhColonMm(availItem.assistanceAfter.getDateTo())

        this.floatoverService.setLoading(false)
        this.floatoverService.setContentHtml(
          `<div>${client.user.fullName()}</div><div><strong>${timeFrom}-${timeTo}</strong></div><div>${availItem.assistanceAfter.addressStart}</div>`,
        )
      })
  }

  showAvailabilityNoteFloatover(availItem: WorkerAvailabilitySelectItem, event?: MouseEvent) {
    const currentMouseX = event?.pageX || 0
    const currentMouseY = event?.pageY || 0

    const noteContent = availItem?.availability?.note || 'Žádná poznámka'

    this.floatoverService.setContentHtml(
      `<div><strong>Poznámka asistenta:</strong></div><br/><div>${noteContent}</div>`,
    )

    // Store the position and show the floatover
    this.floatoverService.setPosition(currentMouseX, currentMouseY)
    this.floatoverService.show()
    this.floatoverService.setLoading(false)
    this.floatoverService.setContentHtml(
      `<div><strong>Poznámka asistenta:</strong></div><br/><div>${availItem.availability.note}</div>`,
    )
  }
  hideAvailabilityNoteFloatover() {
    this.floatoverService.hide()
  }

  private loadData(): Observable<unknown> {
    let assistanceRange: DateRange

    return combineLatest([this.assistanceURID$, this.open$]).pipe(
      // tap(([props, _]) => this.assistanceStash.stash(props as Assistance)),
      // skipWhile(_ => !this.assistanceStash.isChanged()),
      tap((_) => {
        if (this.isModal && !this.modalClosing) this.open()
      }),
      switchMap(([urid, _]) => this.setupAssistanceFromURID(urid)),

      tap((_) => {}),

      tap((assistance) => {
        this.setItemsSelectedFromAssistance(assistance)
        this.initFilter(assistance)
      }),

      // here we need to wait until the window is open and then continue the pipeline

      skipWhile((_) => !this.workerSelectComponent.isOpened),

      tap((_) => {}),

      tap((_) => (this.availabilitiesLoading = true)),
      switchMap((assistance) => {
        assistanceRange = this.getAssistanceRange()
        return this.assistantService.getAllActiveAssistantsWithAssistanceInRange(assistanceRange)
      }),

      switchMap((assistantsToDisable) => {
        return combineLatest([
          this.availabilitiesRecommender.getAvailabilitiesInRange(assistanceRange, assistantsToDisable),
          this.availabilitiesRecommender.getAvailabilitiesForAllActiveAssistants(assistanceRange, assistantsToDisable),
        ])
      }),

      map(([availabilities, allAssistants]) => [
        availabilities,
        this.filterOutFakeAvailabilitiesWhenThereIsARealOne(availabilities, allAssistants),
      ]),
      switchMap(([availabilities, allAssistants]) =>
        this.filterChanged$.pipe(
          log(),
          switchMap(async (filtr) => {
            if (!filtr) return [availabilities, allAssistants]

            const range = filtr.getDateRange()
            const workerStatuses = await firstValueFrom(
              this.feathers.find(WorkerStatus, {
                timeFrom: range.from,
                timeTo: range.to,
              }),
            )

            return Promise.all([
              this.availabilitiesRecommender.filterItems(filtr, availabilities, workerStatuses),
              this.availabilitiesRecommender.filterItems(filtr, allAssistants, workerStatuses),
            ])
          }),
        ),
      ),
      switchMap(([availabilities, allAssistants]) =>
        this.onSearch$.pipe(
          map((searchEvent) => {
            if (!searchEvent.text.length) return [availabilities, allAssistants]
            return [this.search(availabilities, searchEvent), this.search(allAssistants, searchEvent)]
          }),
        ),
      ),
      switchMap(([availabilities, allAssistants]) =>
        this.selectMode$.pipe(
          map((mode) => {
            if (mode === 'availabilities') return [mode, availabilities]
            if (mode === 'allAssistants') return [mode, allAssistants]
          }),
        ),
      ),
      map(([mode, items]) => {
        this.addToSelectedItems(this.selectItemsStash.current)
        return this.mergeSelectedWithRecommended(mode, items, this.itemsSelected)
      }),
      tap((items) => this.availabilityItemStash.stash(items)),
      filter((_) => this.availabilityItemStash.isChanged()),
      tap((items: WorkerAvailabilitySelectItem[]) => this.setItemsRendered(items)),

      tap((_) => (this.availabilitiesLoading = false)),
      tap((items: WorkerAvailabilitySelectItem[]) =>
        this.addAssistancesBeforeAndAfterToRenderedItems(this.assistance, items),
      ),

      takeUntil(this.unsubscribe),
    )
  }

  onClickSelect(event: { component: IonicSelectableComponent; item: any; isSelected: boolean }) {
    // this.stashSelection()

    this.selectItemsStash.stash(event.component._selectedItems)
  }
}
